import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, MessageSquareQuote, Clock, CheckCircle2, Archive, EyeOff, RefreshCw, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";

const CATEGORIES = [
  { v: "generale",     label: "Generale" },
  { v: "portale",      label: "Portale (bug/UX)" },
  { v: "discord",      label: "Discord" },
  { v: "operativo",    label: "Operativo (procedure/turni)" },
  { v: "hr",           label: "Risorse Umane / Promozioni" },
  { v: "bug",          label: "Segnalazione Bug" },
  { v: "suggerimento", label: "Suggerimento / Miglioria" },
];

const STATUS_META = {
  new:      { label: "Nuovo",       cls: "bg-sheriff-gold/15 text-sheriff-gold border-sheriff-gold/40", Icon: Clock },
  reviewed: { label: "In revisione", cls: "bg-blue-500/15 text-blue-300 border-blue-500/40",           Icon: RefreshCw },
  resolved: { label: "Risolto",     cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",  Icon: CheckCircle2 },
  archived: { label: "Archiviato",  cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",           Icon: Archive },
};

export default function FeedbackPanel() {
  const [form, setForm] = useState({ category: "generale", subject: "", message: "", anonymous: false });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/feedback/mine")
      .then(({ data }) => setHistory(data || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (form.subject.trim().length < 3 || form.message.trim().length < 10) {
      toast.error("Inserisci oggetto (min 3) e messaggio (min 10 caratteri).");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", form);
      toast.success("Feedback inviato. Grazie!");
      setForm({ category: "generale", subject: "", message: "", anonymous: false });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio del feedback.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="feedback-panel">
      {/* HEADER */}
      <div>
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <MessageSquareQuote className="h-4 w-4" /> Feedback Operatori
        </div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-[#F5E6B8] mb-2">
          Dicci cosa pensi.
        </h2>
        <p className="text-[#D4C480] max-w-2xl text-sm">
          Segnala problemi, suggerimenti, idee o qualsiasi altra cosa riguardi il Dipartimento —
          portale, Discord, procedure, HR, operatività. Il Comando legge ogni contributo.
        </p>
      </div>

      {/* FORM */}
      <form onSubmit={submit} className="rounded-2xl border border-sheriff-gold/25 bg-[#0F2620]/70 p-6 space-y-4" data-testid="feedback-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold text-[#F5E6B8]/85 mb-1.5 block uppercase tracking-widest">
              Categoria
            </Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="feedback-category-trigger" className="bg-[#143830] border-sheriff-gold/30 text-[#F5E6B8]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.v} value={c.v} data-testid={`feedback-cat-${c.v}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#F5E6B8]/85 mb-1.5 block uppercase tracking-widest">
              Oggetto
            </Label>
            <Input
              data-testid="feedback-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              maxLength={140}
              required
              minLength={3}
              placeholder="Sintesi del feedback…"
              className="bg-[#143830] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-[#F5E6B8]/40"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold text-[#F5E6B8]/85 mb-1.5 block uppercase tracking-widest">
            Messaggio
          </Label>
          <Textarea
            data-testid="feedback-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            maxLength={4000}
            minLength={10}
            required
            rows={6}
            placeholder="Scrivi tutto quello che ti va di segnalare. Il Comando legge personalmente ogni feedback."
            className="bg-[#143830] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-[#F5E6B8]/40 resize-y"
          />
          <div className="mt-1 text-[10px] text-right font-mono text-[#F5E6B8]/45">
            {form.message.length} / 4000
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 pt-2 border-t border-sheriff-gold/15">
          <label className="flex items-center gap-2.5 cursor-pointer group" data-testid="feedback-anon-toggle">
            <Switch
              checked={form.anonymous}
              onCheckedChange={(v) => setForm({ ...form, anonymous: !!v })}
            />
            <span className="text-[13px] text-[#F5E6B8]/85 inline-flex items-center gap-1.5">
              <EyeOff className="h-3.5 w-3.5 text-sheriff-gold/70" />
              Invia in <strong>anonimo</strong>
              <span className="text-[11px] text-[#F5E6B8]/45 ml-1 hidden sm:inline">
                (il Comando non vedrà chi l&apos;ha scritto)
              </span>
            </span>
          </label>
          <Button
            type="submit"
            disabled={sending}
            data-testid="feedback-submit-btn"
            className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold uppercase tracking-widest text-xs rounded-full px-5 h-10"
          >
            {sending
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio…</>
              : <><Send className="h-4 w-4 mr-2" /> Invia Feedback</>}
          </Button>
        </div>
      </form>

      {/* HISTORY */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg text-[#F5E6B8] tracking-tight inline-flex items-center gap-2">
            I tuoi feedback precedenti
            <span className="text-[11px] font-mono uppercase tracking-widest text-sheriff-gold/70">
              ({history.length})
            </span>
          </h3>
          <button
            onClick={load}
            disabled={loading}
            data-testid="feedback-refresh"
            className="text-sheriff-gold hover:text-[#F5E6B8] inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </button>
        </div>

        {loading ? (
          <div className="rounded-xl bg-[#0F2620] border border-sheriff-gold/15 p-8 text-center text-[#F5E6B8]/50 text-sm">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Caricamento cronologia…
          </div>
        ) : history.length === 0 ? (
          <div className="rounded-xl bg-[#0F2620] border border-sheriff-gold/15 p-8 text-center text-[#F5E6B8]/50 text-sm" data-testid="feedback-empty">
            Non hai ancora inviato feedback. Il primo lo puoi scrivere qui sopra.
          </div>
        ) : (
          <div className="space-y-2">
            {history.map((f) => {
              const meta = STATUS_META[f.status] || STATUS_META.new;
              const StatusIcon = meta.Icon;
              const isOpen = expanded === f.id;
              return (
                <div
                  key={f.id}
                  data-testid={`feedback-item-${f.id}`}
                  className="rounded-xl bg-[#0F2620] border border-sheriff-gold/15 hover:border-sheriff-gold/40 transition-colors overflow-hidden"
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : f.id)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3"
                  >
                    <span className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest font-bold ${meta.cls}`}>
                      <StatusIcon className="h-3 w-3" /> {meta.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#F5E6B8] truncate font-medium">{f.subject}</div>
                      <div className="text-[11px] text-[#F5E6B8]/45 font-mono flex items-center gap-2 mt-0.5">
                        <span className="uppercase tracking-widest text-sheriff-gold/70">{f.category}</span>
                        <span>·</span>
                        <span>{new Date(f.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                        {f.anonymous && <><span>·</span><span className="inline-flex items-center gap-0.5"><EyeOff className="h-2.5 w-2.5" />anonimo</span></>}
                      </div>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-sheriff-gold/60 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t border-sheriff-gold/10 space-y-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-sheriff-gold/70 font-bold mb-1">Il tuo messaggio</div>
                        <p className="text-sm text-[#F5E6B8]/85 whitespace-pre-wrap leading-relaxed">{f.message}</p>
                      </div>
                      {f.admin_note && (
                        <div className="rounded-lg bg-sheriff-gold/8 border border-sheriff-gold/30 p-3">
                          <div className="text-[10px] uppercase tracking-widest text-sheriff-gold font-bold mb-1">Risposta del Comando</div>
                          <p className="text-sm text-[#F5E6B8]/90 whitespace-pre-wrap">{f.admin_note}</p>
                          {f.reviewed_at && (
                            <div className="text-[10px] text-[#F5E6B8]/45 font-mono mt-1.5">
                              {new Date(f.reviewed_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
